import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Stack,
  Tooltip,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { FilterAltOff as ClearFilterIcon } from '@mui/icons-material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocalShipping as SupplierIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  Print as PrintIcon,
  FileDownload as ExcelIcon
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { Supplier } from '../../types/suppliers.types';
import suppliersService from '../../services/suppliers.service';
import SupplierDialog from '../../components/suppliers/SupplierDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import config from '../../config';
import { useConfirm } from '../../hooks/useConfirm';
import { toast } from 'react-toastify';

const SuppliersPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const t = {
    title: isRTL ? 'الموردين' : 'Suppliers',
    addSupplier: isRTL ? 'إضافة مورد' : 'Add Supplier',
    code: isRTL ? 'الكود' : 'Code',
    name: isRTL ? 'الاسم' : 'Name',
    mobile: isRTL ? 'الجوال' : 'Mobile',
    email: isRTL ? 'البريد الإلكتروني' : 'Email',
    account: isRTL ? 'الحساب' : 'Account',
    status: isRTL ? 'الحالة' : 'Status',
    actions: isRTL ? 'الإجراءات' : 'Actions',
    search: isRTL ? 'البحث...' : 'Search...',
    loading: isRTL ? 'جاري التحميل...' : 'Loading...',
    noSuppliers: isRTL ? 'لا يوجد موردين' : 'No suppliers found',
    active: isRTL ? 'نشط' : 'Active',
    inactive: isRTL ? 'غير نشط' : 'Inactive',
    totalSuppliers: isRTL ? 'إجمالي الموردين' : 'Total Suppliers',
    activeSuppliers: isRTL ? 'نشط' : 'Active',
    refresh: isRTL ? 'تحديث' : 'Refresh',
    edit: isRTL ? 'تعديل' : 'Edit',
    delete: isRTL ? 'حذف' : 'Delete',
    confirmDelete: isRTL ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?',
    rowsPerPage: isRTL ? 'عدد الصفوف:' : 'Rows per page:'
  };

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<Supplier[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0
  });

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [searchTerm, filterStatus, suppliers]);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const data = await suppliersService.getAll();
      setSuppliers(data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = suppliers;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (supplier) =>
          supplier.supplierCode.toLowerCase().includes(term) ||
          supplier.supplierName.toLowerCase().includes(term) ||
          supplier.supplierNameAr.includes(term) ||
          (supplier.phone && supplier.phone.includes(term)) ||
          (supplier.mobile && supplier.mobile.includes(term)) ||
          (supplier.email && supplier.email.toLowerCase().includes(term))
      );
    }

    if (filterStatus === 'active') result = result.filter(s => s.isActive);
    if (filterStatus === 'inactive') result = result.filter(s => !s.isActive);

    setFilteredSuppliers(result);
    setPage(0);
  };

  const calculateStats = () => {
    const total = suppliers.length;
    const active = suppliers.filter(s => s.isActive).length;
    setStats({ total, active });
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      // Company Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          worksheet.addImage(logoImage, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 60 } });
        } catch {}
      }

      // Company Header
      worksheet.mergeCells('A1:F1');
      const companyCell = worksheet.getCell('A1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 16, bold: true, color: { argb: 'FF5E35B1' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 50;

      // Report Title
      worksheet.mergeCells('A3:F3');
      const titleCell = worksheet.getCell('A3');
      titleCell.value = t.title;
      titleCell.font = { size: 13, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5E35B1' } };
      worksheet.getRow(3).height = 28;

      // Date row
      worksheet.mergeCells('A4:F4');
      const dateCell = worksheet.getCell('A4');
      dateCell.value = `${isRTL ? 'تاريخ التصدير' : 'Exported'}: ${new Date().toLocaleDateString('en-GB')} | ${isRTL ? 'الإجمالي' : 'Total'}: ${filteredSuppliers.length}`;
      dateCell.font = { size: 9, color: { argb: 'FF666666' } };
      dateCell.alignment = { horizontal: 'center' };

      // Headers row 6
      const headers = [t.code, t.name, isRTL ? 'جهة الاتصال' : 'Contact Person', t.mobile, t.email, t.status];
      const headerRow = worksheet.getRow(6);
      headerRow.values = headers;
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 24;

      // Data rows
      let currentRow = 7;
      filteredSuppliers.forEach((s, idx) => {
        const row = worksheet.getRow(currentRow);
        row.values = [
          s.supplierCode,
          isRTL ? s.supplierNameAr : s.supplierName,
          s.contactPerson || '-',
          s.mobile || '-',
          s.email || '-',
          s.isActive ? t.active : t.inactive,
        ];
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        row.height = 20;
        if (idx % 2 === 0) {
          row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
        }
        currentRow++;
      });

      // Column widths
      worksheet.getColumn(1).width = 14;
      worksheet.getColumn(2).width = 30;
      worksheet.getColumn(3).width = 22;
      worksheet.getColumn(4).width = 18;
      worksheet.getColumn(5).width = 28;
      worksheet.getColumn(6).width = 14;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Suppliers_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Excel exported successfully');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(isRTL ? 'فشل التصدير' : 'Excel export failed');
    }
  };

  const handlePrintList = () => {
    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#1976d2;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const rows = filteredSuppliers.map(s => `
      <tr>
        <td>${s.supplierCode}</td>
        <td>${isRTL ? s.supplierNameAr : s.supplierName}</td>
        <td>${s.contactPerson || '-'}</td>
        <td>${s.mobile || '-'}</td>
        <td>${s.email || '-'}</td>
        <td>${s.isActive ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive')}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head>
      <style>
        body{font-family:${isRTL ? 'Cairo,' : ''}Arial,sans-serif;margin:20px;font-size:12px;color:#333;}
        .header{text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;}
        .header h2{color:#1976d2;margin:5px 0;}
        .header p{font-size:11px;color:#666;margin:0;}
        h1{text-align:center;color:#1976d2;margin:10px 0 15px;font-size:18px;}
        table{width:100%;border-collapse:collapse;margin-top:10px;}
        th,td{border:1px solid #ddd;padding:6px 8px;font-size:11px;}
        th{background:#1976d2;color:white;font-weight:bold;}
        tr:nth-child(even){background:#f9f9f9;}
        @page{size:A4 landscape;margin:1cm;}
      </style></head><body>
      ${companyHeader}
      <h1>${t.title}</h1>
      <table>
        <thead><tr>
          <th>${t.code}</th>
          <th>${t.name}</th>
          <th>${isRTL ? 'جهة الاتصال' : 'Contact Person'}</th>
          <th>${t.mobile}</th>
          <th>${t.email}</th>
          <th>${t.status}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="margin-top:10px;font-size:11px;color:#999;">Printed: ${new Date().toLocaleString()}</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };

  const handleAdd = () => {
    setSelectedSupplier(null);
    setDialogOpen(true);
  };

  const handleEdit = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) return;
    try {
      await suppliersService.delete(id);
      loadSuppliers();
    } catch (error) {
      toast.error(isRTL ? 'فشل حذف المورد' : 'Failed to delete supplier');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSupplier(null);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedSuppliers = filteredSuppliers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
                  <Typography variant="body2">{t.totalSuppliers}</Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.active}</Typography>
                  <Typography variant="body2">{t.activeSuppliers}</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Header & Actions */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SupplierIcon sx={{ fontSize: 32, color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={isRTL ? 'تصدير Excel' : 'Export Excel'}>
              <IconButton
                color="success"
                onClick={exportToExcel}
                sx={{ bgcolor: 'rgba(46, 125, 50, 0.1)', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.2)' } }}
              >
                <ExcelIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isRTL ? 'طباعة القائمة' : 'Print List'}>
              <IconButton
                color="primary"
                onClick={handlePrintList}
                sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t.refresh}>
              <IconButton
                color="primary"
                onClick={loadSuppliers}
                sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
                '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' }
              }}
            >
              {t.addSupplier}
            </Button>
          </Stack>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 2 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{isRTL ? 'الحالة' : 'Status'}</InputLabel>
            <Select value={filterStatus} label={isRTL ? 'الحالة' : 'Status'} onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="">{isRTL ? 'الكل' : 'All'}</MenuItem>
              <MenuItem value="active">{t.active}</MenuItem>
              <MenuItem value="inactive">{t.inactive}</MenuItem>
            </Select>
          </FormControl>
          {(searchTerm || filterStatus) && (
            <Button size="small" startIcon={<ClearFilterIcon />} onClick={() => { setSearchTerm(''); setFilterStatus(''); }}>
              {isRTL ? 'مسح' : 'Clear'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: '#667eea' }} />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.code}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.name}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.mobile}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.email}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.account}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSuppliers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{t.noSuppliers}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSuppliers.map((supplier) => (
                  <TableRow key={supplier.supplierId} hover sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.05)' } }}>
                    <TableCell>
                      <Typography fontWeight="bold" color="#667eea">{supplier.supplierCode}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {isRTL ? supplier.supplierNameAr : supplier.supplierName}
                      </Typography>
                      {supplier.contactPerson && (
                        <Typography variant="caption" color="text.secondary">
                          {supplier.contactPerson}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{supplier.mobile || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {supplier.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {supplier.accountCode ? (
                        <>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {supplier.accountCode}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {isRTL ? supplier.accountNameAr : supplier.accountName}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" component="div" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={supplier.isActive ? t.active : t.inactive}
                        color={supplier.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={t.edit}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(supplier)}
                            sx={{ color: 'primary.main', '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.delete}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(supplier.supplierId)}
                            sx={{ color: 'error.main', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={filteredSuppliers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t.rowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            isRTL ? `${from}-${to} من ${count}` : `${from}-${to} of ${count}`
          }
        />
      </TableContainer>

      <SupplierDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={loadSuppliers}
        supplier={selectedSupplier}
      />
    </Box>
  );
};

export default SuppliersPage;